import { useEffect, useState } from 'react'
import { Button, Input, Grid, Separator } from '@design-systems/canvas-ui'
import _ from 'lodash'
import Icon, {
  PlusCircleIcon,
  MinusCircleIcon,
} from '@design-systems/react-icons'
import {
  SectionWrapper,
  renderControlLabel,
  showErrorMessage,
  renderControlLabelSmallHeaderOnly,
  getUsNumberFormat,
} from '../Builders'
import { AutocompleteInput } from '../../common/AutocompleteInput'
import {
  buildDropdownOptions,
  getControlError,
  isControlError,
} from '../../common/helper'
import { renderRFI3QuestionWindow } from '../../common/rfiHelper'
import { getRfiTagValues } from '../../common/common'
import { isValidWholeNumber } from 'mvscommoncomponents'
import { VALUE_OTHERS } from '../../common/commonConstants'

const SECTION_TOTAL_EMP_BY_GENDER = 'total_employees_by_gender'
const SECTION_TOTAL_EMP_BY_DEMOGRAPHICS = 'total_employees_by_demographics'
const CONTROL_UID_MALE = 'number_of_male'
const CONTROL_UID_FEMALE = 'number_of_female'
const CONTROL_UID_LOCAL_EMP = 'local_employees'
const CONTROL_UID_FOREIGN_EMP = 'foreign_employees'
const LABEL_MALE = 'Male'
const LABEL_FEMALE = 'Female'
const LABEL_LOCAL_EMP = 'Local Employees'
const LABEL_FOREIGN_EMP = 'Foreign Employees'
const LABEL_EMP_DESIGNATION = 'Employee Designation'
const LABEL_NO_OF_EMP = 'Number of Employees'

const EmployeeInformation = (props) => {
  const [sectionData, setSectionData] = useState(null)
  const [isTriggerSave, setIsTriggerSave] = useState(false)

  useEffect(() => {
    setSectionData(props.sectionData)
  }, [props.sectionData])

  useEffect(() => {
    if (isTriggerSave && !_.isEqual(props.sectionData, sectionData)) {
      props.saveRfiPayload(sectionData, props.sectionTemplate.uid)
    }
  }, [isTriggerSave]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * To update the Total Employees Count
   * @returns
   */
  const getTotalEmployeeCount = () => {
    return sectionData?.employee_strength_list
      ?.map((emp) => Number(emp.number_of_employees))
      .reduce((prev, curr) => prev + curr, 0)
  }

  const onInputChangeHandler = (value, metadata) => {
    let groupUID = metadata?.inputMetadata?.groupUID

    setSectionData({
      ...sectionData,
      [groupUID]: {
        ...sectionData[groupUID],
        [metadata.uid]: value,
      },
    })
  }

  /**
   * Render the Employee Information body
   * @returns
   */
  const renderEmployeeInformationBody = () => {
    let empByGenderMaleError,
      empByGenderFemaleError,
      empByDemoLocalError,
      empByDemoForeignError

    if (!props.isRfi3) {
      empByGenderMaleError = isControlError(
        `${props.sectionPath}.total_employees_by_gender.number_of_male`,
        props.errorCache,
      )
      empByGenderFemaleError = getControlError(
        `${props.sectionPath}.total_employees_by_gender.number_of_female`,
        props.errorCache,
      )
      empByDemoLocalError = isControlError(
        `${props.sectionPath}.total_employees_by_demographics.local_employees`,
        props.errorCache,
      )
      empByDemoForeignError = getControlError(
        `${props.sectionPath}.total_employees_by_demographics.foreign_employees`,
        props.errorCache,
      )
    }

    let sectionContent = (
      <div>
        {/* Employee Designation & Number of Employees */}
        {renderEmployeeDesignationCountView()}

        {/* Total Employees */}
        <Grid.Container className="hc8-pa-2x">
          <Grid.Item size={{ xs: 3 }}>
            <Input.Label className="hc8-fs-150 hc8-clr-placeholder">
              Total Employees
            </Input.Label>
            <Input.Label className="hc8-fs-100 hc8-mt-2x hc8-mb-6x">
              {getUsNumberFormat(getTotalEmployeeCount())}
            </Input.Label>
          </Grid.Item>
        </Grid.Container>

        {/* Total Employees By Gender */}
        <Input.Label className="hc8-fs-150 font-weight-500 hc8-mh-2x">
          Total Employees By Gender
        </Input.Label>
        <Grid.Container className="hc8-mb-3x hc8-mt-4x hc8-ph-2x">
          <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
            <div style={{ width: '100%' }}>
              {props.isReadonly
                ? renderControlLabelSmallHeaderOnly({ label_name: LABEL_MALE })
                : renderControlLabel({ label_name: LABEL_MALE })}
              {props.isReadonly ? (
                <Input.Label className="hc8-fs-100 hc8-mt-2x">
                  {getUsNumberFormat(
                    sectionData?.total_employees_by_gender?.number_of_male,
                  )}
                </Input.Label>
              ) : (
                <Input.Text
                  className="hc8-mt-2x"
                  data-testid="tb-male-emp"
                  value={sectionData?.total_employees_by_gender?.number_of_male}
                  isErrored={empByGenderMaleError}
                  onChange={(event) => {
                    let newCount = event.target.value
                    var isValidNumber = isValidWholeNumber(newCount)
                    newCount =
                      newCount === '' || isValidNumber
                        ? newCount
                        : sectionData?.total_employees_by_gender?.number_of_male
                          ? sectionData?.total_employees_by_gender
                              ?.number_of_male
                          : ''

                    let updateMetadata = {
                      uid: CONTROL_UID_MALE,
                      inputMetadata: {
                        groupUID: SECTION_TOTAL_EMP_BY_GENDER,
                      },
                    }
                    onInputChangeHandler(newCount, updateMetadata)
                  }}
                />
              )}
            </div>
          </Grid.Item>

          <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
            <div style={{ width: '100%' }}>
              {props.isReadonly
                ? renderControlLabelSmallHeaderOnly({
                    label_name: LABEL_FEMALE,
                  })
                : renderControlLabel({ label_name: LABEL_FEMALE })}
              {props.isReadonly ? (
                <Input.Label className="hc8-fs-100 hc8-mt-2x">
                  {getUsNumberFormat(
                    sectionData?.total_employees_by_gender?.number_of_female,
                  )}
                </Input.Label>
              ) : (
                <Input.Text
                  className="hc8-mt-2x"
                  data-testid="tb-female-emp"
                  value={
                    sectionData?.total_employees_by_gender?.number_of_female
                  }
                  isErrored={empByGenderFemaleError}
                  onChange={(event) => {
                    let newCount = event.target.value
                    var isValidNumber = isValidWholeNumber(newCount)
                    newCount =
                      newCount === '' || isValidNumber
                        ? newCount
                        : sectionData?.total_employees_by_gender
                              ?.number_of_female
                          ? sectionData?.total_employees_by_gender
                              ?.number_of_female
                          : ''

                    let updateMetadata = {
                      uid: CONTROL_UID_FEMALE,
                      inputMetadata: {
                        groupUID: SECTION_TOTAL_EMP_BY_GENDER,
                      },
                    }
                    onInputChangeHandler(newCount, updateMetadata)
                  }}
                />
              )}
            </div>
          </Grid.Item>
          {!props.isReadonly &&
            (empByGenderMaleError || empByGenderFemaleError) && (
              <div className="hc8-pt-8x">
                {showErrorMessage(
                  getRfiTagValues(
                    'totalEmployeeInformationGenderMismatchMessage',
                  ),
                )}
              </div>
            )}
        </Grid.Container>

        {/* Total Employees By Demographics */}
        <Input.Label className="hc8-fs-150 font-weight-500 hc8-ma-2x">
          Total Employees By Demographics
        </Input.Label>
        <Grid.Container className="hc8-mb-3x hc8-mt-4x hc8-ph-2x">
          <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
            <div style={{ width: '100%' }}>
              {props.isReadonly
                ? renderControlLabelSmallHeaderOnly({
                    label_name: LABEL_LOCAL_EMP,
                  })
                : renderControlLabel({ label_name: LABEL_LOCAL_EMP })}
              {props.isReadonly ? (
                <Input.Label className="hc8-fs-100 hc8-mt-2x">
                  {getUsNumberFormat(
                    sectionData?.total_employees_by_demographics
                      ?.local_employees,
                  )}
                </Input.Label>
              ) : (
                <Input.Text
                  className="hc8-mt-2x"
                  data-testid="tb-local-emp"
                  value={
                    sectionData?.total_employees_by_demographics
                      ?.local_employees
                  }
                  isErrored={empByDemoLocalError}
                  onChange={(event) => {
                    let newCount = event.target.value
                    var isValidNumber = isValidWholeNumber(newCount)
                    newCount =
                      newCount === '' || isValidNumber
                        ? newCount
                        : sectionData?.total_employees_by_demographics
                              ?.local_employees
                          ? sectionData.total_employees_by_demographics
                              .local_employees
                          : ''

                    let updateMetadata = {
                      uid: CONTROL_UID_LOCAL_EMP,
                      inputMetadata: {
                        groupUID: SECTION_TOTAL_EMP_BY_DEMOGRAPHICS,
                      },
                    }
                    onInputChangeHandler(newCount, updateMetadata)
                  }}
                />
              )}
            </div>
          </Grid.Item>

          <Grid.Item size={{ xs: 3 }} className="hc8-pt-none">
            <div style={{ width: '100%' }}>
              {props.isReadonly
                ? renderControlLabelSmallHeaderOnly({
                    label_name: LABEL_FOREIGN_EMP,
                  })
                : renderControlLabel({ label_name: LABEL_FOREIGN_EMP })}
              {props.isReadonly ? (
                <Input.Label className="hc8-fs-100 hc8-mt-2x">
                  {getUsNumberFormat(
                    sectionData?.total_employees_by_demographics
                      ?.foreign_employees,
                  )}
                </Input.Label>
              ) : (
                <Input.Text
                  className="hc8-mt-2x"
                  data-testid="tb-foreign-emp"
                  value={
                    sectionData?.total_employees_by_demographics
                      ?.foreign_employees
                  }
                  isErrored={empByDemoForeignError}
                  onChange={(event) => {
                    let newCount = event.target.value
                    var isValidNumber = isValidWholeNumber(newCount)
                    newCount =
                      newCount === '' || isValidNumber
                        ? newCount
                        : sectionData?.total_employees_by_demographics
                              ?.foreign_employees
                          ? sectionData.total_employees_by_demographics
                              .foreign_employees
                          : ''

                    let updateMetadata = {
                      uid: CONTROL_UID_FOREIGN_EMP,
                      inputMetadata: {
                        groupUID: SECTION_TOTAL_EMP_BY_DEMOGRAPHICS,
                      },
                    }
                    onInputChangeHandler(newCount, updateMetadata)
                  }}
                />
              )}
            </div>
          </Grid.Item>
          {!props.isReadonly &&
            (empByDemoLocalError || empByDemoForeignError) && (
              <div className="hc8-pt-8x">
                {showErrorMessage(
                  getRfiTagValues(
                    'totalEmployeeInformationDemographicsMismatchMessage',
                  ),
                )}
              </div>
            )}
        </Grid.Container>
      </div>
    )

    return props.isRfi3
      ? renderRFI3QuestionWindow(
          props.isRfi3Readonly,
          sectionContent,
          sectionData?.rfi3_answers,
          props.sectionTemplate,
          props.rfiMasterData,
          (sectionRfi3Answers) =>
            setSectionData({
              ...sectionData,
              rfi3_answers: sectionRfi3Answers,
            }),
          props.supplierId,
          props.locationId,
          props.manufacturingTypeId,
          sectionData?.rfi3_files,
          (sectionRfiFiles) => {
            setIsTriggerSave(true)
            setSectionData({
              ...sectionData,
              rfi3_files: sectionRfiFiles,
            })
          },
          props.errorCache,
        )
      : sectionContent
  }

  const updateEmpRowProperty = (group, property, rowIndex, newVal) => {
    let updatedDCGroupRows = sectionData[group].map((eachRow, index) => {
      return rowIndex === index
        ? {
            ...eachRow,
            [property]: newVal ? newVal : null,
          }
        : eachRow
    })
    setSectionData({
      ...sectionData,
      [group]: updatedDCGroupRows,
    })
  }

  const renderEmployeeDesignationCountView = () => {
    let empInfoPath = `${props.sectionPath}.employee_strength_list`

    let empInfoRows = sectionData?.employee_strength_list.map(
      (eachEmpInfoRow, eachEmpInfoIndex) => {
        let empDesignationIndex = getControlError(
          `${empInfoPath}[${eachEmpInfoIndex}].employee_designation`,
          props.errorCache,
        )
        let empCountIndex = getControlError(
          `${empInfoPath}[${eachEmpInfoIndex}].number_of_employees`,
          props.errorCache,
        )
        let empDesignationOthersIndex = getControlError(
          `${empInfoPath}[${eachEmpInfoIndex}].other_employee_designation`,
          props.errorCache,
        )

        return (
          <Grid.Container
            className="hc8-pl-3x hc8-mb-2x background-grey border-radius"
            spacing="none"
          >
            <Grid.Item size={{ xs: 3 }} className="hc8-pl-none hc8-pt-3x">
              <div style={{ width: '100%' }}>
                {props.isReadonly
                  ? renderControlLabelSmallHeaderOnly({
                      label_name: LABEL_EMP_DESIGNATION,
                    })
                  : renderControlLabel({
                      label_name: LABEL_EMP_DESIGNATION,
                      is_mandatory: true,
                    })}
                {props.isReadonly ? (
                  <Input.Label className="hc8-fs-100 hc8-mt-2x">
                    {eachEmpInfoRow.employee_designation
                      ? eachEmpInfoRow.employee_designation
                      : ''}
                  </Input.Label>
                ) : (
                  <AutocompleteInput
                    key={`ac-designation-${eachEmpInfoIndex}`}
                    value={
                      eachEmpInfoRow.employee_designation
                        ? eachEmpInfoRow.employee_designation
                        : ''
                    }
                    class="font-size-16 height-32p hc8-mt-2x"
                    error={empDesignationIndex}
                    options={buildDropdownOptions(
                      props.rfiMasterData,
                      {
                        nodeDotPath: 'rfi_location.emp_designation',
                      },
                      true,
                      true,
                    )}
                    onUpdate={(value) => {
                      updateEmpRowProperty(
                        'employee_strength_list',
                        'employee_designation',
                        eachEmpInfoIndex,
                        value?.value,
                      )
                    }}
                  />
                )}
                {empDesignationIndex && (
                  <div className="hc8-pt-2x">
                    {showErrorMessage(empDesignationIndex)}
                  </div>
                )}
              </div>
              {eachEmpInfoRow?.employee_designation?.toLowerCase() ===
                VALUE_OTHERS && (
                <div>
                  {props.isReadonly ? (
                    <Input.Label className="hc8-fs-100 hc8-mt-2x">
                      {eachEmpInfoRow.other_employee_designation
                        ? eachEmpInfoRow.other_employee_designation
                        : ''}
                    </Input.Label>
                  ) : (
                    <>
                      <Input.Text
                        key={`ac-other-employee-${eachEmpInfoIndex}`}
                        className="hc8-mt-2x"
                        placeholder={'Mention employee designation'}
                        value={
                          eachEmpInfoRow.other_employee_designation
                            ? eachEmpInfoRow.other_employee_designation
                            : ''
                        }
                        isErrored={empDesignationOthersIndex}
                        onChange={(event) => {
                          updateEmpRowProperty(
                            'employee_strength_list',
                            'other_employee_designation',
                            eachEmpInfoIndex,
                            event.target.value,
                          )
                        }}
                      />
                      <div className="hc8-pt-2x">
                        {showErrorMessage(empDesignationOthersIndex)}
                      </div>
                    </>
                  )}
                </div>
              )}
            </Grid.Item>
            <Grid.Item size={{ xs: 3 }} className="hc8-pt-3x hc8-pl-3x">
              <div style={{ width: '100%' }}>
                {props.isReadonly
                  ? renderControlLabelSmallHeaderOnly({
                      label_name: LABEL_NO_OF_EMP,
                    })
                  : renderControlLabel({
                      label_name: LABEL_NO_OF_EMP,
                      is_mandatory: true,
                    })}
                {props.isReadonly ? (
                  <Input.Label
                    key={`ac-count-${eachEmpInfoIndex}`}
                    className="hc8-fs-100 hc8-mt-2x"
                  >
                    {eachEmpInfoRow.number_of_employees
                      ? getUsNumberFormat(eachEmpInfoRow.number_of_employees)
                      : ''}
                  </Input.Label>
                ) : (
                  <>
                    <Input.Text
                      key={`ac-count-${eachEmpInfoIndex}`}
                      className="hc8-mt-2x"
                      value={
                        eachEmpInfoRow.number_of_employees
                          ? eachEmpInfoRow.number_of_employees
                          : ''
                      }
                      isErrored={empCountIndex}
                      onChange={(event) => {
                        let newCount = event.target.value
                        var isValidNumber = isValidWholeNumber(newCount)
                        newCount =
                          newCount === '' || isValidNumber
                            ? newCount
                            : eachEmpInfoRow.number_of_employees
                              ? eachEmpInfoRow.number_of_employees
                              : ''

                        updateEmpRowProperty(
                          'employee_strength_list',
                          'number_of_employees',
                          eachEmpInfoIndex,
                          newCount,
                        )
                      }}
                    />
                    <div className="hc8-pt-2x">
                      {showErrorMessage(empCountIndex)}
                    </div>
                  </>
                )}
              </div>
            </Grid.Item>
            {!props.isReadonly && eachEmpInfoIndex > 0 && (
              <Grid.Item size={{ xs: 1 }} className="align-centre-left hc8-pt-4x">
                <Button
                  key={`delete-dc-row-${eachEmpInfoIndex}`}
                  id={`delete-dc-row-${eachEmpInfoIndex}`}
                  data-testid={`delete-dc-row-${eachEmpInfoIndex}`}
                  onClick={() => {
                    let data = [...sectionData.employee_strength_list]
                    data.splice(eachEmpInfoIndex, 1)
                    setSectionData({
                      ...sectionData,
                      employee_strength_list: data,
                    })
                  }}
                  variant="ghost"
                  size="sm"
                  isIconOnly
                  className="hc8-ml-6x"
                  aria-label="Delete design centre location row"
                >
                  <Icon svg={MinusCircleIcon} aria-hidden />
                </Button>
              </Grid.Item>
            )}
          </Grid.Container>
        )
      },
    )

    return (
      <div>
        {empInfoRows}
        <Separator />
        {!props.isReadonly && (
          <Button
            key="btn-add-dc-row"
            id="btn-add-dc-row"
            data-testid="btn-add-dc-row"
            variant="ghost"
            className="hc8-pl-none hc8-mt-1x hc8-mb-4x"
            onClick={() => addEmployeeInfoRow()}
          >
            <Icon svg={PlusCircleIcon} className="hc8-mr-2x" aria-hidden />
            Add More
          </Button>
        )}
      </div>
    )
  }

  /**
   * Add new Employee Information row
   */
  const addEmployeeInfoRow = () => {
    setSectionData({
      ...sectionData,
      employee_strength_list: [
        ...sectionData.employee_strength_list,
        {
          employee_designation: '',
          number_of_employees: '',
        },
      ],
    })
  }

  return (
    <SectionWrapper
      sectionTemplate={props.sectionTemplate}
      setIsTriggerSave={
        !props.isReadonly || props.isRfi3 ? setIsTriggerSave : () => {}
      }
      sectionBody={renderEmployeeInformationBody()}
      sectionPath={props.sectionPath}
      isRfi3={props.isRfi3}
      errorCache={props.errorCache}
    />
  )
}

export default EmployeeInformation
